import React from "react";

export interface OutfitCardProps {
  id: string;
  occasion: string;
  imageUrl?: string | null;
  outfitItems?: Array<{
    id: string;
    imageUrl?: string | null;
    style: string;
    category?: {
      id: string;
      name: string;
    };
  }>;
  createdAt?: string;
  updatedAt?: string;
  favorited?: boolean;
  onClick?: () => void;
  onFavoriteToggle?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const OutfitCard: React.FC<OutfitCardProps> = ({
  id,
  occasion,
  imageUrl,
  outfitItems,
  createdAt,
  updatedAt,
  favorited = false,
  onClick,
  onFavoriteToggle,
  onDelete,
}) => {
  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card onClick
    onFavoriteToggle?.(id);
  };
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card onClick
    onDelete?.(id);
  };
  return (
    <div
      className="group relative bg-white dark:bg-gray-900 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:scale-105 backdrop-blur-sm"
      onClick={onClick}
      data-testid={`outfit-card-${id}`}
    >
      {/* Gradient overlay for luxury effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 via-purple-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

      {/* Image section with elegant styling */}
      <div className="relative overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={occasion}
            className="w-full h-48 object-cover bg-gray-100 dark:bg-gray-800 transition-transform duration-300 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-48 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 text-gray-400 dark:text-gray-500">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 opacity-30">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <span className="text-xs font-light tracking-wider uppercase">
                No Image
              </span>
            </div>
          </div>
        )}

        {/* Header action buttons */}
        <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
          {onDelete && (
            <button
              onClick={handleDeleteClick}
              className="p-2 rounded-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 hover:bg-white dark:hover:bg-gray-900 hover:scale-110 transition-all duration-300"
              aria-label="Delete outfit"
              data-testid={`delete-outfit-${id}`}
              title="Delete outfit"
            >
              <svg
                className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-red-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m-8 0v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V7" />
              </svg>
            </button>
          )}
          {onFavoriteToggle && (
            <button
              onClick={handleFavoriteClick}
              className="p-2 rounded-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 hover:bg-white dark:hover:bg-gray-900 hover:scale-110 transition-all duration-300 group/heart"
              aria-label={favorited ? "Remove from favorites" : "Add to favorites"}
              title={favorited ? "Remove from favorites" : "Add to favorites"}
            >
              <svg
                className={`w-4 h-4 transition-all duration-300 ${
                  favorited
                    ? "text-red-500 fill-red-500 scale-110"
                    : "text-gray-400 dark:text-gray-500 hover:text-red-400 group-hover/heart:scale-110"
                }`}
                viewBox="0 0 24 24"
                fill={favorited ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Subtle gradient overlay on image */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Content section with luxury typography */}
      <div className="p-6 flex-1 flex flex-col relative">
        {/* Decorative gradient line */}
        <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent" />

        <h3 className="text-lg font-light tracking-wider uppercase text-gray-900 dark:text-gray-100 mb-2 truncate group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
          {occasion}
        </h3>

        {/* Outfit Items Thumbnails */}
        {outfitItems && outfitItems.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-light text-gray-500 dark:text-gray-400 tracking-wider uppercase mb-2">
              Items ({outfitItems.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {outfitItems.slice(0, 4).map((item) => (
                <div
                  key={item.id}
                  className="relative w-12 h-12 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
                  title={item.style}
                >
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.style}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
              {outfitItems.length > 4 && (
                <div className="w-12 h-12 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <span className="text-xs font-light text-gray-500 dark:text-gray-400">
                    +{outfitItems.length - 4}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-auto flex items-center justify-between">
          {/* Elegant arrow indicator */}
          <div className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-all duration-300 group-hover:translate-x-1">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
              />
            </svg>
          </div>
        </div>

        {/* Subtle bottom gradient line */}
        <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent opacity-50" />
      </div>
    </div>
  );
};

export default OutfitCard;
